import React, { PureComponent } from 'react';

import { FormattedMessage } from 'react-intl';

import {
  withAnalyticsEvents,
  WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import { Grid, GridColumn } from '@atlaskit/page';
import { fireScreenAnalytics } from '@atlassian/analytics-bridge';

import { FocusedPageHeader } from 'src/components/accessibility';
import { COMMIT_LIST_LOAD } from 'src/metrics';
import { CompareButton } from 'src/sections/repository/components/compare-button';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import CloneButton from 'src/sections/repository/containers/clone-button';
import { Filter } from 'src/sections/repository/sections/commit-list/types';
import { MetricProvider } from 'src/utils/metrics-provider';

import CheckoutButton from '../containers/checkout-button';
import CommitListWithKeyboardShortcuts from '../containers/commit-list';

import EmptyState from './empty-state';
import Filters from './filters';
import messages from './page.i18n';

type Props = {
  nextUrl?: string;
  previousUrl?: string;
  refName?: string;
  repositoryFullSlug: string;
  onFilterChange: (filter: Partial<Filter>) => void;
  onClickShowAll?: () => void;
  query: Filter;
  isEmpty: boolean;
};

export class CommitListPage extends PureComponent<
  Props & WithAnalyticsEventsProps
> {
  componentDidMount() {
    this.fireScreenEvent();
  }

  componentDidUpdate(prevProps: Props) {
    const { refName } = this.props;
    if (refName !== prevProps.refName) {
      this.fireScreenEvent();
    }
  }

  fireScreenEvent() {
    if (this.props.createAnalyticsEvent) {
      fireScreenAnalytics(this.props.createAnalyticsEvent({}), {
        allBranches: !this.props.refName,
      });
    }
  }

  renderCommitsList = () => {
    const { nextUrl, previousUrl, refName, repositoryFullSlug } = this.props;
    const { page, search } = this.props.query;
    const isFilterApplied = page > 1 || !!search;

    return (
      <>
        <FocusedPageHeader
          actions={
            <ButtonGroup>
              <CompareButton
                repositoryFullSlug={repositoryFullSlug}
                source="commitsScreen"
              />
              {refName ? <CheckoutButton refName={refName} /> : <CloneButton />}
            </ButtonGroup>
          }
          breadcrumbs={<Breadcrumbs />}
          bottomBar={
            <Filters
              repositoryFullSlug={repositoryFullSlug}
              refName={refName}
              onFilterChange={this.props.onFilterChange}
              onClickShowAll={this.props.onClickShowAll}
              filter={this.props.query}
            />
          }
        >
          <FormattedMessage {...messages.pageTitle} />
        </FocusedPageHeader>
        <CommitListWithKeyboardShortcuts
          nextUrl={nextUrl}
          previousUrl={previousUrl}
          isFilterApplied={isFilterApplied}
          repositoryFullSlug={repositoryFullSlug}
        />
      </>
    );
  };

  renderEmptyState = () => {
    const { refName } = this.props;
    return (
      <>
        <FocusedPageHeader
          actions={
            refName ? <CheckoutButton refName={refName} /> : <CloneButton />
          }
          breadcrumbs={<Breadcrumbs />}
        >
          <FormattedMessage {...messages.pageTitle} />
        </FocusedPageHeader>
        <EmptyState />
      </>
    );
  };

  render() {
    return (
      <MetricProvider metric={COMMIT_LIST_LOAD}>
        <Grid layout="fluid" spacing="comfortable">
          <GridColumn>
            {this.props.isEmpty
              ? this.renderEmptyState()
              : this.renderCommitsList()}
          </GridColumn>
        </Grid>
      </MetricProvider>
    );
  }
}

export default withAnalyticsEvents()(CommitListPage);
